// apiSourcesService.ts
// Service functions for API Sources feature

import { 
  ApiSourceId, 
  TestStatus, 
  TestResultsResponse, 
  ApiTestRequest 
} from '../types/apiSources';
import { authenticatedFetch } from './authenticatedFetch';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Run API tests for selected endpoints
 */
export async function runApiTests(
  api: ApiSourceId,
  endpoints: string[],
  startDate?: string,
  endDate?: string,
  limit?: number
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await authenticatedFetch(`${API_BASE}/api/dashboard/${api}/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoints,
        start_date: startDate,
        end_date: endDate,
        limit
      } as ApiTestRequest),
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const error = await response.json();
        errorMessage = error.message || error.error || errorMessage;
      } catch {
        // If response is not JSON, try text
        try {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        } catch {
          // Use default error message
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message || 'Tests started successfully'
    };
  } catch (error) {
    console.error(`[API Sources] Error running ${api} tests:`, error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to start tests';
    
    // Provide more helpful error messages
    if (errorMessage.includes('Not authenticated') || errorMessage.includes('must be logged in')) {
      return {
        success: false,
        message: 'Please log in to run API tests. Your session may have expired.'
      };
    }
    
    return {
      success: false,
      message: errorMessage
    };
  }
}

/**
 * Poll test status (call repeatedly until isRunning is false)
 */
export async function pollTestStatus(api: ApiSourceId): Promise<TestStatus> {
  try {
    const response = await authenticatedFetch(`${API_BASE}/api/dashboard/${api}/status`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return {
      isRunning: data.running || false,
      progress: data.progress,
      currentEndpoint: data.currentEndpoint,
      startTime: data.startTime,
      message: data.message
    };
  } catch (error) {
    console.error(`[API Sources] Error checking ${api} status:`, error);
    return {
      isRunning: false,
      message: 'Failed to check status'
    };
  }
}

/**
 * Get test results after tests complete
 */
export async function getTestResults(api: ApiSourceId): Promise<TestResultsResponse> {
  try {
    const response = await authenticatedFetch(`${API_BASE}/api/dashboard/${api}/results`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    
    // Map API response to TestResult format
    const allResults = data.all_results || data.results || [];
    const mappedResults = allResults.map((result: any) => ({
      endpoint: result.endpoint,
      method: result.method,
      description: result.description,
      category: result.category,
      success: result.success,
      statusCode: result.status_code,
      responseTime: result.response_time_ms,
      recordCount: result.record_count,
      error: result.error,
      timestamp: result.timestamp
    }));
    
    return {
      success: true,
      results: mappedResults,
      totalTests: data.total_endpoints || data.summary?.total || 0,
      successCount: data.successful || data.summary?.success || 0,
      failureCount: data.failed || data.summary?.failed || 0,
      timestamp: data.test_timestamp || data.timestamp
    };
  } catch (error) {
    console.error(`[API Sources] Error fetching ${api} results:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch results'
    };
  }
}

/**
 * Download CSV file for a specific test run
 */
export function downloadCsv(api: ApiSourceId, folder: string): void {
  const url = `${API_BASE}/api/dashboard/${api}/download/${encodeURIComponent(folder)}`;
  const link = document.createElement('a');
  link.href = url;
  link.download = `${api}_results.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export actual API data (not test results)
 */
export async function exportData(
  api: ApiSourceId,
  endpoints: string[],
  startDate: string,
  endDate: string
): Promise<{ success: boolean; message: string; csvPath?: string }> {
  try {
    const response = await authenticatedFetch(`${API_BASE}/api/dashboard/${api}/export-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoints,
        start_date: startDate,
        end_date: endDate
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message || 'Data exported successfully',
      csvPath: data.csvPath
    };
  } catch (error) {
    console.error(`[API Sources] Error exporting ${api} data:`, error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to export data'
    };
  }
}

/**
 * Poll test status with auto-retry (convenience function)
 */
export async function pollUntilComplete(
  api: ApiSourceId,
  onProgress?: (status: TestStatus) => void,
  maxAttempts: number = 300, // 10 minutes at 2s intervals
  intervalMs: number = 2000
): Promise<boolean> {
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    const status = await pollTestStatus(api);
    
    if (onProgress) {
      onProgress(status);
    }
    
    if (!status.isRunning) {
      return true; // Completed
    }
    
    await new Promise(resolve => setTimeout(resolve, intervalMs));
    attempts++;
  }
  
  return false; // Timeout
}
